import { numberIfFinite } from 'utils/numbers';

import { axiosB2 } from './axiosB2';

const DEFAULT_PAGE_SIZE = 50;
const ENTITY_COLLECTION_ORDER_FIELDS = new Set([
    'description',
    'id',
    'observations',
    'accountCount',
    'contactCount',
    'audit.created.date',
    'audit.modified.user.name',
]);
const ENTITY_COLLECTION_ITEM_ORDER_FIELDS = new Set([
    'account.name',
    'account.responsible',
    'account.companyType',
    'account.email',
    'account.marketingStatus',
    'contact.name',
    'contact.surname',
    'contact.accountName',
    'contact.email',
    'contact.marketingStatus',
    'discardedBy',
    'discardedAtUTC',
]);

/**
 * Parse B2 JSON/string envelope; throws on parse failure, empty body, or non-success State.
 * @returns {object} parsed envelope
 */
function assertB2EnvelopeSuccess(data) {
    let parsed;
    try {
        parsed = typeof data === 'string' ? JSON.parse(data) : data;
    } catch {
        throw new Error('Invalid B2 response: JSON parse error');
    }
    if (parsed == null) {
        throw new Error('Empty B2 response');
    }
    const state = parsed.State ?? parsed.state;
    if (state !== undefined && state !== null && state !== '1' && state !== 1) {
        const msg = parsed.Message || parsed.message || 'B2 error';
        const error = new Error(typeof msg === 'string' ? msg : 'B2 error');
        error.errorCode = parsed.ErrorCode ?? parsed.errorCode;
        throw error;
    }
    return parsed;
}

function extractResult(parsed) {
    if (parsed.Result !== undefined) {
        return parsed.Result;
    }
    if (parsed.result !== undefined) {
        return parsed.result;
    }
    return parsed;
}

function normalizePagination({ offset = 0, limit = DEFAULT_PAGE_SIZE } = {}) {
    const intMaxResults = numberIfFinite(limit);
    const intFirstRecortPosition = numberIfFinite(offset);
    return {
        intMaxResults: intMaxResults > 0 ? intMaxResults : DEFAULT_PAGE_SIZE,
        intFirstRecortPosition: intFirstRecortPosition >= 0 ? intFirstRecortPosition : 0,
    };
}

function normalizeSearch(search) {
    return typeof search === 'string' ? search.trim() : '';
}

function normalizeEntityType(entityType) {
    const normalized = String(entityType || 'accounts').toLowerCase();
    return normalized === 'contact' || normalized === 'contacts' ? 'contacts' : 'accounts';
}

function normalizeAddEntityCollectionEntityType(entityType) {
    return normalizeEntityType(entityType);
}

/**
 * @param {number[]|string|string[]} entityIds
 * @returns {string[]}
 */
function normalizeEntityIds(entityIds) {
    if (Array.isArray(entityIds)) {
        return entityIds
            .map((id) => numberIfFinite(id))
            .filter((id) => id > 0)
            .map((id) => String(id));
    }
    if (typeof entityIds === 'string') {
        return entityIds
            .split(',')
            .map((id) => numberIfFinite(id.trim()))
            .filter((id) => id > 0)
            .map((id) => String(id));
    }
    const single = numberIfFinite(entityIds);
    return single > 0 ? [String(single)] : [];
}

function hasOperatorPayload(operatorInfo) {
    if (operatorInfo === undefined || operatorInfo === null) {
        return false;
    }
    if (typeof operatorInfo === 'string') {
        const trimmed = operatorInfo.trim();
        return trimmed.length > 0 && trimmed !== '{}' && trimmed !== '[]';
    }
    if (typeof operatorInfo === 'object') {
        return Object.keys(operatorInfo).length > 0;
    }
    return false;
}

function normalizeOrderInfo(orderInfo, allowedFields) {
    const field = typeof orderInfo?.Field === 'string' ? orderInfo.Field.trim() : '';
    const direction =
        typeof orderInfo?.Direction === 'string' ? orderInfo.Direction.trim().toUpperCase() : '';

    if (!allowedFields.has(field) || (direction !== 'ASC' && direction !== 'DESC')) {
        return null;
    }

    return {
        Field: field,
        Direction: direction,
    };
}

function postB2WithEnvelope(method, payload) {
    return axiosB2.post(method, payload).then(({ data }) => {
        const parsed = assertB2EnvelopeSuccess(data);
        return {
            result: extractResult(parsed),
            envelope: parsed,
        };
    });
}

function postB2(method, payload) {
    return postB2WithEnvelope(method, payload).then(({ result }) => result);
}

/**
 * @param {unknown} result
 * @returns {number|null}
 */
export function normalizeEntityCollectionId(result) {
    if (result == null || result === '') {
        return null;
    }

    const directId = numberIfFinite(result);
    if (typeof directId === 'number' && directId > 0) {
        return directId;
    }

    if (typeof result === 'object') {
        const nestedId = numberIfFinite(
            result.id ?? result.Id ?? result.collectionId ?? result.CollectionId,
        );
        if (typeof nestedId === 'number' && nestedId > 0) {
            return nestedId;
        }
    }

    if (typeof result === 'string') {
        const parsedId = numberIfFinite(result.trim());
        if (typeof parsedId === 'number' && parsedId > 0) {
            return parsedId;
        }
    }

    return null;
}

/**
 * GetEntityCollections — paginated list with optional title search.
 * @param {{
 *   search?: string,
 *   offset?: number,
 *   limit?: number,
 *   token?: string,
 *   whereInfo?: object,
 *   operatorInfo?: object|string,
 *   orderInfo?: { Field: string, Direction: 'ASC'|'DESC' }
 * }} params
 * @returns {Promise<{ items: object[], hasMore: boolean, total?: number|null }>}
 */
export function getEntityCollections({
    search = '',
    offset = 0,
    limit = DEFAULT_PAGE_SIZE,
    token,
    whereInfo,
    operatorInfo,
    orderInfo,
} = {}) {
    const pagination = normalizePagination({ offset, limit });
    const payload = {
        strSearchParamaters: normalizeSearch(search),
        ...pagination,
    };

    const normalizedToken = typeof token === 'string' ? token.trim() : '';
    if (normalizedToken) {
        payload.token = normalizedToken;
    }
    if (whereInfo && typeof whereInfo === 'object') {
        payload.whereInfo = whereInfo;
    }
    if (hasOperatorPayload(operatorInfo)) {
        payload.operatorInfo =
            typeof operatorInfo === 'string' ? operatorInfo : JSON.stringify(operatorInfo);
    }
    const normalizedOrderInfo = normalizeOrderInfo(orderInfo, ENTITY_COLLECTION_ORDER_FIELDS);
    if (normalizedOrderInfo) {
        payload.orderInfo = normalizedOrderInfo;
    }

    return postB2('GetEntityCollections', payload).then((result) => {
        const items = Array.isArray(result)
            ? result
            : Array.isArray(result?.items)
              ? result.items
              : [];
        const totalRows = result?.totalRows ?? result?.TotalRows ?? result?.total ?? null;
        const parsedTotal = numberIfFinite(totalRows);

        return {
            items,
            hasMore: items.length >= pagination.intMaxResults,
            total: typeof parsedTotal === 'number' && parsedTotal >= 0 ? parsedTotal : null,
        };
    });
}

/**
 * GetEntityCollection — single collection header by ID.
 * @param {{ collectionId: number|string }} params
 */
export function getEntityCollection({ collectionId }) {
    return postB2('GetEntityCollection', {
        collectionId: numberIfFinite(collectionId),
    });
}

/**
 * CreateEntityCollection — header only (no initial members).
 * @param {{ descripcion: string, observations?: string }} params
 * @returns {Promise<number|string>}
 */
export function createEntityCollection({ descripcion, observations = '' }) {
    return postB2('CreateEntityCollection', {
        descripcion: String(descripcion ?? '').trim(),
        observations: observations ?? '',
    }).then((result) => {
        const collectionId = normalizeEntityCollectionId(result);
        if (collectionId == null) {
            throw new Error('CreateEntityCollection returned no id');
        }
        return collectionId;
    });
}

/**
 * UpdateEntityCollection — update title and notes.
 * @param {{ collectionId: number|string, descripcion: string, observations?: string }} params
 */
export function updateEntityCollection({ collectionId, descripcion, observations = '' }) {
    return postB2('UpdateEntityCollection', {
        collectionId: numberIfFinite(collectionId),
        descripcion: String(descripcion ?? '').trim(),
        observations: observations ?? '',
    });
}

/**
 * DeleteEntityCollection — logical delete of collection and all items.
 * @param {{ collectionId: number|string }} params
 */
export function deleteEntityCollection({ collectionId }) {
    return postB2('DeleteEntityCollection', {
        collectionId: numberIfFinite(collectionId),
    });
}

/**
 * CloneEntityCollection — duplicate collection header, observations and active members.
 * @param {{ collectionId: number|string }} params
 * @returns {Promise<number>}
 */
export function cloneEntityCollection({ collectionId }) {
    return postB2('CloneEntityCollection', {
        collectionId: numberIfFinite(collectionId),
    }).then((result) => {
        const newCollectionId = normalizeEntityCollectionId(result);
        if (newCollectionId == null) {
            throw new Error('CloneEntityCollection returned no id');
        }
        return newCollectionId;
    });
}

/**
 * GetEntityCollectionItems — members of a collection by entity type.
 * @param {{
 *   collectionId: number|string,
 *   entityType: 'accounts'|'contacts'|'account'|'contact',
 *   search?: string,
 *   discarded?: boolean,
 *   offset?: number,
 *   limit?: number,
 *   orderInfo?: { Field: string, Direction: 'ASC'|'DESC' },
 * }} params
 * @returns {Promise<{ items: object[], hasMore: boolean, total: number|null }>}
 */
export function getEntityCollectionItems({
    collectionId,
    entityType,
    search = '',
    discarded = false,
    offset = 0,
    limit = DEFAULT_PAGE_SIZE,
    orderInfo,
} = {}) {
    const pagination = normalizePagination({ offset, limit });
    const payload = {
        collectionId: numberIfFinite(collectionId),
        entityType: normalizeEntityType(entityType),
        whereInfo: {
            SearchParamaters: normalizeSearch(search),
            ...(discarded ? { Discarded: true } : {}),
        },
        ...pagination,
    };
    const normalizedOrderInfo = normalizeOrderInfo(orderInfo, ENTITY_COLLECTION_ITEM_ORDER_FIELDS);
    if (normalizedOrderInfo) {
        payload.orderInfo = normalizedOrderInfo;
    }

    return postB2WithEnvelope('GetEntityCollectionItems', payload).then(({ result, envelope }) => {
        const items = Array.isArray(result)
            ? result
            : Array.isArray(result?.items)
              ? result.items
              : Array.isArray(result?.Items)
                ? result.Items
                : [];
        const parsedTotal = numberIfFinite(
            result?.total ??
                result?.Total ??
                result?.totalRows ??
                result?.TotalRows ??
                envelope?.totalRows ??
                envelope?.TotalRows,
        );
        const total = typeof parsedTotal === 'number' && parsedTotal >= 0 ? parsedTotal : null;
        const isEndOfFile =
            numberIfFinite(result?.EOF ?? result?.eof ?? envelope?.EOF ?? envelope?.eof) === -1;
        return {
            items,
            hasMore:
                total != null
                    ? pagination.intFirstRecortPosition + items.length < total
                    : !isEndOfFile && items.length >= pagination.intMaxResults,
            total,
        };
    });
}

/**
 * AddEntityCollectionItems — add members (idempotent; duplicates ignored).
 * @param {{
 *   collectionId: number|string,
 *   entityType: 'accounts'|'contacts'|'account'|'contact',
 *   entityIds?: number[]|string|string[],
 *   token?: string,
 *   replaceExisting?: boolean
 * }} params
 */
export function addEntityCollectionItems({
    collectionId,
    entityType,
    entityIds,
    token,
    replaceExisting,
}) {
    const normalizedToken = typeof token === 'string' ? token.trim() : '';
    const payload = {
        collectionId: numberIfFinite(collectionId),
        entityType: normalizeAddEntityCollectionEntityType(entityType),
    };

    if (normalizedToken) {
        payload.token = normalizedToken;
    } else {
        payload.entityIds = normalizeEntityIds(entityIds);
    }

    if (typeof replaceExisting === 'boolean') {
        payload.replaceExisting = replaceExisting;
    }

    return postB2('AddEntityCollectionItems', payload);
}

/**
 * RemoveEntityCollectionItems — remove members (logical delete).
 * @param {{
 *   collectionId: number|string,
 *   entityType: 'accounts'|'contacts'|'account'|'contact',
 *   entityIds?: number[]|string|string[],
 *   token?: string
 * }} params
 */
export function removeEntityCollectionItems({ collectionId, entityType, entityIds, token }) {
    const normalizedToken = typeof token === 'string' ? token.trim() : '';
    const payload = {
        collectionId: numberIfFinite(collectionId),
        entityType: normalizeEntityType(entityType),
    };

    if (normalizedToken) {
        payload.token = normalizedToken;
    } else {
        payload.entityIds = normalizeEntityIds(entityIds);
    }

    return postB2('RemoveEntityCollectionItems', payload);
}
